"use client";

/**
 * The contest page's Institutions tab: `EX-INS01`, `docs/29` §6.25, the canvas
 * board `SeasonBar.dc.html` (its top half).
 *
 * Three cards (the champion, the best institution, the most first solves) and
 * the table: each institution placed by its best entry, with its entries, the
 * solves of all of them and its places in the top three. Every number is the
 * server's, folded from the board a contestant reads, so a frozen round shows
 * the frozen board's table and says so.
 */
import { useTranslations } from "next-intl";
import { Award, Building2, Snowflake, Sparkles, Trophy } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatNumber } from "@/lib/format-datetime";
import { cn } from "@/lib/utils";
import { useContestInstitutions } from "../../hooks";
import { institutionMark } from "../../lib/season";

/** The top three's marks: gold, silver and bronze, each with its own lightness. */
export const MEDAL_TONES: Readonly<Record<number, string>> = {
  1: "bg-[#D4AF37]",
  2: "bg-[#B8BEC8]",
  3: "bg-[#C8885A]",
};

function HeadCard({
  label,
  name,
  note,
  mark,
  tone,
  testId,
}: {
  label: string;
  name: string;
  note: string;
  mark: ReactNode;
  tone: "gold" | "ink" | "mint";
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      className="flex min-w-0 items-center gap-4 rounded-2xl border border-[rgb(var(--border))] bg-white p-5 shadow-card transition-shadow duration-200 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
    >
      <span
        aria-hidden
        className={cn(
          "flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[14px] font-heading text-[17px] font-bold",
          tone === "gold" && "bg-[#F6E7B0] text-[#6B4E00]",
          tone === "ink" && "bg-neutral-950 text-white",
          tone === "mint" && "bg-emerald-50 text-emerald-700",
        )}
      >
        {mark}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs font-medium uppercase tracking-[0.07em] text-neutral-500">{label}</span>
        <span className="truncate font-heading text-[17px] font-bold text-neutral-900">{name}</span>
        <span className="truncate text-[13px] text-neutral-500">{note}</span>
      </div>
    </div>
  );
}

export function InstitutionsPanel({ slug, locale }: { slug: string; locale: string }) {
  const t = useTranslations("contest.season");
  const query = useContestInstitutions(slug);
  const n = (value: number) => formatNumber(value, locale);

  if (query.isLoading) {
    return (
      <div className="space-y-6" data-testid="institutions-loading">
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-[94px] rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="rounded-2xl border border-[rgb(var(--border))] bg-white p-8 text-center shadow-card" role="alert">
        <p className="text-sm text-neutral-700">{t("institutions.loadFailed")}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => void query.refetch()}>
          {t("retry")}
        </Button>
      </div>
    );
  }

  const data = query.data;
  const team = data.participation === "team";
  const best = data.rows[0];
  const name = (value: string | null) => value ?? t("unnamed");

  return (
    <div className="space-y-6" data-testid="institutions-panel">
      {data.frozen ? (
        <p
          className="flex items-start gap-2.5 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900"
          data-testid="institutions-frozen"
        >
          <Snowflake className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {t("institutions.frozen")}
        </p>
      ) : null}

      <div className="grid gap-4 md:grid-cols-3">
        {data.champion ? (
          <HeadCard
            testId="institutions-champion"
            label={t("institutions.champion")}
            name={name(data.champion.name)}
            note={
              data.champion.institution
                ? t("institutions.championNote", { institution: data.champion.institution, solved: n(data.champion.solved) })
                : t("institutions.solvedNote", { solved: n(data.champion.solved) })
            }
            mark={<Trophy className="h-6 w-6" />}
            tone="gold"
          />
        ) : null}
        {best ? (
          <HeadCard
            testId="institutions-best"
            label={t("institutions.bestInstitution")}
            name={best.institution}
            note={t(team ? "institutions.bestNoteTeams" : "institutions.bestNote", {
              entries: n(best.entries),
              solved: n(best.solved),
            })}
            mark={institutionMark(best.institution)}
            tone="ink"
          />
        ) : null}
        {data.mostFirstSolves ? (
          <HeadCard
            testId="institutions-first"
            label={t("institutions.mostFirstSolves")}
            name={name(data.mostFirstSolves.name)}
            note={
              data.mostFirstSolves.institution
                ? t("institutions.firstNote", {
                    institution: data.mostFirstSolves.institution,
                    count: n(data.mostFirstSolves.count),
                  })
                : t("institutions.firstNotePlain", { count: n(data.mostFirstSolves.count) })
            }
            mark={<Sparkles className="h-6 w-6" />}
            tone="mint"
          />
        ) : null}
      </div>

      <section
        aria-labelledby="institutions-title"
        className="overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-white shadow-card"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pb-2 pt-4">
          <h2 id="institutions-title" className="font-heading text-[19px] font-bold text-neutral-900">
            {t("institutions.title")}
          </h2>
          <span className="text-[13px] text-neutral-500">{t(team ? "institutions.leadTeam" : "institutions.lead")}</span>
        </div>
        {data.rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 pb-10 pt-6 text-center" data-testid="institutions-empty">
            <Building2 className="h-8 w-8 text-neutral-300" aria-hidden />
            <p className="text-sm text-neutral-600">{t("institutions.empty")}</p>
          </div>
        ) : (
          <Table className="text-[14.5px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 pl-5">{t("institutions.rank")}</TableHead>
                <TableHead>{t("institutions.institution")}</TableHead>
                <TableHead>{t(team ? "institutions.bestTeam" : "institutions.bestEntry")}</TableHead>
                <TableHead className="text-right">{t("institutions.bestPlace")}</TableHead>
                <TableHead className="text-right">{t(team ? "institutions.teams" : "institutions.entries")}</TableHead>
                <TableHead className="text-right">{t(team ? "institutions.solvesTeams" : "institutions.solves")}</TableHead>
                <TableHead>{t("institutions.medals")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((row) => (
                <TableRow key={row.institution} className="transition-colors hover:bg-neutral-50/70" data-testid="institution-row">
                  <TableCell className="pl-5 tabular-nums text-neutral-500">{n(row.rank)}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-2.5">
                      <span
                        aria-hidden
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-neutral-100 font-heading text-[11px] font-bold text-neutral-700"
                      >
                        {institutionMark(row.institution)}
                      </span>
                      <span className="font-medium text-neutral-900">{row.institution}</span>
                    </span>
                  </TableCell>
                  <TableCell className="text-neutral-700">{name(row.best.name)}</TableCell>
                  <TableCell className="text-right tabular-nums">{n(row.best.place)}</TableCell>
                  <TableCell className="text-right tabular-nums">{n(row.entries)}</TableCell>
                  <TableCell className="text-right tabular-nums">{n(row.solved)}</TableCell>
                  <TableCell>
                    <span className="inline-flex gap-1">
                      {row.medals.map((place, index) => (
                        <span
                          key={`${place}-${index}`}
                          role="img"
                          aria-label={t("institutions.medal", { place: String(place) })}
                          className={cn("inline-flex h-[22px] w-[22px] items-center justify-center rounded-full", MEDAL_TONES[place])}
                        >
                          <Award className="h-3 w-3 text-neutral-900/70" aria-hidden />
                        </span>
                      ))}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {data.withoutInstitution > 0 ? (
          <p className="border-t border-neutral-100 px-5 py-3 text-xs text-neutral-500" data-testid="institutions-without">
            {t("institutions.without", { count: n(data.withoutInstitution) })}
          </p>
        ) : null}
      </section>
    </div>
  );
}
